<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    header="Scan Results"
    :modal="true"
    :closable="true"
    :style="{ width: '90vw', maxWidth: '800px' }"
    :breakpoints="{ '960px': '95vw' }"
  >
    <div class="scan-results-container">
      <!-- Scanned Data Display -->
      <div class="scanned-data-section">
        <h3 class="text-lg font-semibold mb-3">Scanned Information</h3>
        <div class="grid">
          <div class="col-12 md:col-6">
            <div class="field">
              <label class="font-medium">Name</label>
              <div class="text-lg">
                {{ scannedData.firstName }}
                <template v-if="scannedData.middleName"> {{ scannedData.middleName }} </template>
                {{ scannedData.lastName }}
              </div>
            </div>
          </div>
          <div class="col-12 md:col-6">
            <div class="field">
              <label class="font-medium">ID Number</label>
              <div class="text-lg">{{ scannedData.idNumber }}</div>
            </div>
          </div>
          <div class="col-12 md:col-6" v-if="scannedData.address">
            <div class="field">
              <label class="font-medium">Address</label>
              <div>{{ scannedData.address }}</div>
            </div>
          </div>
          <div class="col-12 md:col-6" v-if="scannedData.city || scannedData.state">
            <div class="field">
              <label class="font-medium">City/State</label>
              <div>
                {{ scannedData.city }}{{ scannedData.city && scannedData.state ? ', ' : ''
                }}{{ scannedData.state }}
              </div>
            </div>
          </div>
          <div class="col-12 md:col-6" v-if="scannedData.dateOfBirth">
            <div class="field">
              <label class="font-medium">Date of Birth</label>
              <div>{{ scannedData.dateOfBirth }}</div>
            </div>
          </div>
          <div class="col-12 md:col-6" v-if="scannedData.expirationDate">
            <div class="field">
              <label class="font-medium">Expiration Date</label>
              <div>{{ scannedData.expirationDate }}</div>
            </div>
          </div>
          <div class="col-12">
            <div class="field">
              <label class="font-medium">Barcode Hash</label>
              <div class="text-sm font-mono p-2 rounded break-all">
                {{ scannedData.barcodeHash }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Matching Persons -->
      <div class="matches-section mt-4">
        <h3 class="text-lg font-semibold mb-3">Matching Persons ({{ matchingPersons.length }})</h3>

        <div v-if="matchingPersons.length === 0" class="text-center p-4 text-gray-500">
          <i class="pi pi-user-plus text-4xl mb-2"></i>
          <p>No matching persons found. This appears to be a new contact.</p>
        </div>

        <div v-else class="matching-persons-list">
          <div
            v-for="person in matchingPersons"
            :key="person.id"
            class="person-card p-3 border-1 surface-border border-round mb-2 hover:surface-100 cursor-pointer"
            @click="selectPerson(person)"
          >
            <div class="flex align-items-center justify-content-between">
              <div class="flex align-items-center">
                <i class="pi pi-user text-primary mr-3"></i>
                <div>
                  <div class="font-semibold">{{ person.firstName }} {{ person.lastName }}</div>
                  <div class="text-sm text-gray-600">
                    ID: {{ person.idNumber || 'N/A' }}
                    <span v-if="person.email" class="ml-2">• {{ person.email }}</span>
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    Match reason:
                    <span
                      v-if="person.barcodeHash === scannedData.barcodeHash"
                      class="text-green-600 font-medium"
                    >
                      Same ID (Barcode Hash Match)
                    </span>
                    <span v-else class="text-blue-600 font-medium"> Name Match </span>
                  </div>
                </div>
              </div>
              <Button
                icon="pi pi-check"
                class="p-button-sm p-button-outlined"
                @click.stop="selectPerson(person)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between w-full">
        <Button
          label="Create New Person"
          icon="pi pi-plus"
          class="p-button-outlined"
          @click="createNewPerson"
        />
        <div class="flex gap-2">
          <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="onCancel" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import type { ScannedIdData } from '@/composables/useIdScanner'
import type { Person } from '@/types/Person'

interface Props {
  visible: boolean
  scannedData: ScannedIdData
  matchingPersons: (Person & { matchReason?: string })[]
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'select-person', person: Person): void
  (e: 'create-new-person', data: ScannedIdData): void
  (e: 'cancel'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Computed property to sort matches by relevance
const sortedMatches = computed(() => {
  return [...props.matchingPersons].sort((a, b) => {
    // Prioritize barcode hash matches
    const aIsHashMatch = a.barcodeHash === props.scannedData.barcodeHash
    const bIsHashMatch = b.barcodeHash === props.scannedData.barcodeHash

    if (aIsHashMatch && !bIsHashMatch) return -1
    if (!aIsHashMatch && bIsHashMatch) return 1

    // Then sort by name similarity
    const aNameMatch =
      `${a.firstName} ${a.lastName}`.toLowerCase() ===
      `${props.scannedData.firstName} ${props.scannedData.lastName}`.toLowerCase()
    const bNameMatch =
      `${b.firstName} ${b.lastName}`.toLowerCase() ===
      `${props.scannedData.firstName} ${props.scannedData.lastName}`.toLowerCase()

    if (aNameMatch && !bNameMatch) return -1
    if (!aNameMatch && bNameMatch) return 1

    return 0
  })
})

function selectPerson(person: Person) {
  emit('select-person', person)
  emit('update:visible', false)
}

function createNewPerson() {
  emit('create-new-person', props.scannedData)
  emit('update:visible', false)
}

function onCancel() {
  emit('cancel')
  emit('update:visible', false)
}
</script>

<style scoped>
.scan-results-container {
  max-height: 70vh;
  overflow-y: auto;
}

.scanned-data-section {
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.matches-section {
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.field {
  margin-bottom: 1rem;
}

.field label {
  display: block;
  margin-bottom: 0.5rem;
  color: #495057;
}

.person-card {
  transition: background-color 0.2s ease;
}

.person-card:hover {
  background-color: #f8f9fa;
}

.matching-persons-list {
  max-height: 300px;
  overflow-y: auto;
}
</style>
